import { Outlet } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CreateTrainingFormQuestionDocument,
  FormFragment,
  QuestionnaireFragment,
  UpdateTrainingFormQuestionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Questionnaire from 'modules/questionnaires/components/Questionnaire'
import { QuestionnaireContent } from 'modules/questionnaires/lib/types'
import {
  serializeFormsQuestionnaireElementInput,
  serializeQuestionnaireResponseContent,
  translations,
} from 'modules/trainings/lib/trainingFormQuestions'

import { PreviewSidepanel } from '../../Preview'

type Props = {
  openSidepanel: boolean
  setOpenSidepanel: (value: boolean) => void
  questions: QuestionnaireContent
  trainingForm: FormFragment
  questionnaire: QuestionnaireFragment[]
  questionsId?: string
}
export const NonPublishedQuestions = ({
  openSidepanel,
  setOpenSidepanel,
  questions,
  questionnaire,
  trainingForm,
  questionsId,
}: Props) => {
  const { company } = useCurrentSession()

  const [createFormQuestionsTraining] = useMutation(CreateTrainingFormQuestionDocument, {
    extractorFn: (data) => data.formsMutations.createFormsQuestion,
  })

  const [updateFormQuestionsTraining] = useMutation(UpdateTrainingFormQuestionDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsQuestion,
  })

  const handleQuestionnaireChange = async (content: QuestionnaireContent) => {
    const questionnaireElements = serializeFormsQuestionnaireElementInput(content)
    let response
    if (!trainingForm) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.surveys.create.error.survey_error'),
      })
      return Promise.reject()
    }
    if (!!questions.length && questionsId) {
      response = await updateFormQuestionsTraining({
        variables: {
          formId: trainingForm.id,
          questionnaire: questionnaireElements,
          resourceId: trainingForm.resourceId,
          id: questionsId,
        },
      })
      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.question.error'),
        })
      })
      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.surveys.create.question.updated_successfully'),
        })
      }
    } else {
      response = await createFormQuestionsTraining({
        variables: {
          formId: trainingForm.id,
          companyId: company.id.toString(),
          questionnaire: questionnaireElements,
          resourceId: trainingForm.id,
        },
      })
      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.question.error'),
        })
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.surveys.create.question.success'),
        })
      }
    }

    return content
  }

  return (
    <Box>
      <PreviewSidepanel
        open={openSidepanel}
        onClose={() => setOpenSidepanel(!open)}
        currentPreview={serializeQuestionnaireResponseContent(questionnaire)}
      />
      <Questionnaire
        content={questions || []}
        translations={translations()}
        onChange={handleQuestionnaireChange}
        formType={trainingForm?.formType}
      />
      <Outlet />
    </Box>
  )
}
